import React, { MutableRefObject, ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { CallRecIntegration, LanguageErrorCodes } from '../../../i18n'
import { callRecIntegrationByGUIDURL, callRecIntegrationURL } from '../../NonReact/constants/ApiConstants'
import { HIDE } from '../../NonReact/constants/class-names'
import { EMPTY_STRING } from '../../NonReact/constants/Common'
import { RESTMethods } from '../../NonReact/constants/REST'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { IsVarEmpty } from '../../NonReact/utils/GlobalFunctions'
import {
    DeepMap,
    FieldError,
    FieldValues,
    FormProvider,
    useForm,
    UseFormMethods
} from '../../Vendor/react-hook-form/dist/index.ie11'
import { RESTAPIError } from '../Common/API'
import { getSession } from '../Common/API/CommonAPI'
import { ALL_VALUE, EMPTY_VALUE } from '../Common/API/ItemsGetters'
import { StatusCodes } from '../../NonReact/common/API/REST/RESTAPIClient'
import { requestJSON } from '../../NonReact/common/API/REST/RESTClient'
// Utilities imports.
import { IconName } from '../Common/Components/Icon'
// import {SelectItem} from '../Common/Components/Selects/Types'
import Tabs from '../Common/Components/Tabs'
import { Tab } from '../Common/Components/Tabs/Types'
import { ON_CHANGE } from '../Common/Constants/HookFormEventNames'
// Contexts imports.
import { LanguageContext, MessageContext, ReactState } from '../Common/Contexts'
import FooterActions from './Actions/FooterActions'
import {
    CALLRECINTEGRATIONID,
    DEFAULT_DUBBER_LOCATION_URL,
    HASHED_PASSWORD_STRING,
    INTEGRATION_TYPE_VALUES
} from './Constants/Constants'
import stateMapper from './mappers/stateMapper'
import { ApplicationCallRecIntegrationData, CallRecIntegrationResponse, FilterValues } from './Types/Types'
import { CommunicationsSettings } from './Views/CommunicationsSettings'
import { DubberCommunicationsSettings } from './Views/DubberCommunicationsSettings'
import General from './Views/General'

import { DEFAULT_CME_ID } from '../Common/Constants/ServerValues'
import ScopeAssignment from './Views/ScopeAssignment'
import { SessionAPIResponse } from '../../NonReact/Types/Types'
import { MessageContextType } from '../Common/Contexts/MessageContext'
import { JSONResponse } from '../../NonReact/common/Types/Types'

/**
 * Component properties type.
 */
type Props = {
    viewOnly?: boolean
    closeModal: () => void
    filters?: FilterValues
    selectedCallRecIntegration?: Nullable<CallRecIntegrationResponse>
    refreshAction: () => void
}

/**
 * Configuration tabs container component.
 *
 * @param closeModal Modal close callback.
 * @param selectedCalRecIntegration The selected Directory from the legacy Directory list page.
 * @param refresh A function to refresh the list page on submission of the form.
 * @param filters Object containing the filter values for the list page.
 */
const CallRecIntegrationTabs: React.FC<Props> = ({
    closeModal,
    selectedCallRecIntegration,
    filters,
    refreshAction,
    viewOnly
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(LanguageErrorCodes.NAME)
    language.loadBundle(CallRecIntegration.NAME)

    // Context that allows error messages to be displayed on the modal
    const { displayError }: MessageContextType = useContext(MessageContext)

    // React Ref to keep track of the modal being in view
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const [isValidationPopperOpen, setIsValidationPopperOpen]: ReactState<boolean> = useState<boolean>(false)

    // Active tab ID.
    const [activeTabID, setActiveTabID]: ReactState<number> = useState<number>(
        CALLRECINTEGRATIONID.CALL_REC_INTEGRATION
    )

    // State to store any form errors
    const [serverError, setServerError]: ReactState<Definable<string>> = useState<string>()

    const [integrationType, setIntegrationType]: ReactState<number> = useState<number>(ALL_VALUE)
    const [sessionNonce, setSessionNonce]: ReactState<Definable<string>> = useState<Definable<string>>()

    // If value is null or undefined then return a specified default value, if not then return the value
    const getValueForDefault = useCallback(
        <T extends string | number | number[]>(value: Nullable<T>, defaultValue: T): T => {
            if (Array.isArray(value)) {
                return value && value.length === 1 ? value : defaultValue
            }
            return value || defaultValue
        },
        []
    )

    /**
     * Resolves the form value for a password field on an existing integration: the hashed-password placeholder
     * when the password is already stored server-side, otherwise the supplied default. This keeps a stored
     * password represented in the form without exposing it, mirroring how the backend reports each password via
     * its `*Provided` flag.
     *
     * @param selectedCRI The integration being edited, or null/undefined when adding a new one.
     * @param isPasswordProvided Whether this specific password is already stored server-side.
     * @param defaultValue Value to use when no password is provided (or when adding a new integration).
     * @returns The hashed-password placeholder or the default value.
     */
    const getHashedPasswordOrDefault = useCallback(
        (
            selectedCRI: Nullable<CallRecIntegrationResponse>,
            isPasswordProvided: Definable<boolean>,
            defaultValue: string
        ): string => {
            if (IsVarEmpty(selectedCRI)) return defaultValue

            return isPasswordProvided ? HASHED_PASSWORD_STRING : defaultValue
        },
        []
    )

    useEffect((): void => {
        getSession().then((res: JSONResponse<SessionAPIResponse>): void => {
            if (isMounted.current) {
                setSessionNonce(res?.Nonce)
            }
        })
    }, [])

    // Sets the default values for the form within the Modal.
    const defaultValues: MutableRefObject<ApplicationCallRecIntegrationData> = useRef({
        callRecIntegrationDetails: {
            guid: IsVarEmpty(selectedCallRecIntegration)
                ? EMPTY_VALUE
                : getValueForDefault(selectedCallRecIntegration.CallRecIntGUID, EMPTY_VALUE),
            integrationType: IsVarEmpty(selectedCallRecIntegration)
                ? ALL_VALUE
                : selectedCallRecIntegration?.CallRecIntType,
            description: IsVarEmpty(selectedCallRecIntegration)
                ? EMPTY_VALUE
                : getValueForDefault(selectedCallRecIntegration.Description, EMPTY_VALUE)
        },
        scopeAssignments: {
            cme: getValueForDefault(filters?.cme, DEFAULT_CME_ID),
            telephonyServer: !IsVarEmpty(selectedCallRecIntegration)
                ? selectedCallRecIntegration?.TelSysID ?? ALL_VALUE
                : getValueForDefault(filters?.telSysID, ALL_VALUE),
            partition: !IsVarEmpty(selectedCallRecIntegration)
                ? selectedCallRecIntegration?.PartIDInTelSys ?? ALL_VALUE
                : getValueForDefault(filters?.partitionID, ALL_VALUE),
            filterRecursively: true
        },
        communicationsSettings: {
            mainIpAddress: EMPTY_VALUE,
            secondIpAddress: EMPTY_VALUE,
            MainAuthenticationDetailsUsername: EMPTY_VALUE,
            MainAuthenticationDetailsPassword: EMPTY_VALUE,
            SecondaryAuthenticationDetailsUsername: EMPTY_VALUE,
            SecondaryAuthenticationDetailsPassword: EMPTY_VALUE,
            apiParametersOne: EMPTY_VALUE,
            apiParametersTwo: EMPTY_VALUE
        },
        dubberCommunicationsSettings: {
            dubberLocationUrl: IsVarEmpty(selectedCallRecIntegration)
                ? DEFAULT_DUBBER_LOCATION_URL
                : getValueForDefault(
                      selectedCallRecIntegration?.APICommunicationSettings?.IPAddrOrHost1,
                      DEFAULT_DUBBER_LOCATION_URL
                  ),
            developerAuthUsername: IsVarEmpty(selectedCallRecIntegration)
                ? EMPTY_STRING
                : getValueForDefault(selectedCallRecIntegration?.APICommunicationSettings?.AuthUserID1, EMPTY_STRING),
            developerAuthPassword: getHashedPasswordOrDefault(
                selectedCallRecIntegration,
                selectedCallRecIntegration?.APICommunicationSettings?.AuthPassword1Provided,
                EMPTY_STRING
            ),
            accountAuthUsername: IsVarEmpty(selectedCallRecIntegration)
                ? EMPTY_VALUE
                : getValueForDefault(selectedCallRecIntegration.APICommunicationSettings.AuthUserID2, EMPTY_VALUE),
            accountAuthPassword: getHashedPasswordOrDefault(
                selectedCallRecIntegration,
                selectedCallRecIntegration?.APICommunicationSettings?.AuthPassword2Provided,
                EMPTY_STRING
            ),
            apiParametersOne: IsVarEmpty(selectedCallRecIntegration)
                ? EMPTY_VALUE
                : getValueForDefault(selectedCallRecIntegration.APICommunicationSettings.APIParameter1, EMPTY_VALUE),
            apiParametersTwo: IsVarEmpty(selectedCallRecIntegration)
                ? EMPTY_VALUE
                : getValueForDefault(selectedCallRecIntegration.APICommunicationSettings.APIParameter2, EMPTY_VALUE)
        }
    })
    // Form context.
    const methods: UseFormMethods = useForm({
        defaultValues: defaultValues.current,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    const {
        handleSubmit,
        errors,
        formState: { isValid, dirtyFields }
    }: UseFormMethods = methods

    /**
     * Tab activation method.
     * @param newActiveTabID ID of tab to activate.
     */
    const activateTab = (newActiveTabID: number): void => {
        setActiveTabID(newActiveTabID)
    }

    const determineValidationErrors = (formErrors: DeepMap<FieldValues, FieldError>): boolean[] => [
        !!formErrors?.callRecIntegrationDetails,
        !!formErrors?.scopeAssignments,
        !!formErrors?.communicationsSettings,
        !!formErrors?.dubberCommunicationsSettings
    ]

    const validationErrors: boolean[] = determineValidationErrors(errors)

    const submitCRIConfiguration = async (state: ApplicationCallRecIntegrationData): Promise<void> => {
        // Will fire if save action was successful - Zero errors
        const successfulSubmit = (): void => {
            // Close Modal and Refresh the list page.
            closeModal()
            refreshAction()
        }

        // Will fire if save action was not successful - One or more errors
        const failedSubmit = (error: RESTAPIError): void => {
            const { errorCode, errorMessage, HTTPStatus }: RESTAPIError = error

            if (HTTPStatus === StatusCodes.OK || HTTPStatus === StatusCodes.OK_NO_CONTENT) {
                displayError(error)
                return successfulSubmit()
            }

            const errorCodeLanguage: Nullable<string> = (LanguageErrorCodes as unknown as Record<string, string>)[
                `ErrorCode_${errorCode}`
            ]

            setServerError(errorCodeLanguage ? language.get(errorCodeLanguage) : errorMessage)
            setIsSubmitting(false)

            return undefined
        }

        if (IsVarEmpty(selectedCallRecIntegration)) {
            requestJSON(
                callRecIntegrationURL(
                    `${state?.scopeAssignments?.telephonyServer}`,
                    `${state?.scopeAssignments?.partition}`
                ),
                {
                    method: RESTMethods.POST,
                    body: stateMapper(state, sessionNonce)
                }
            )
                .then(successfulSubmit)
                .catch(failedSubmit)
        } else {
            requestJSON(callRecIntegrationByGUIDURL(state?.callRecIntegrationDetails?.guid ?? EMPTY_VALUE), {
                method: RESTMethods.PUT,
                body: stateMapper(state, sessionNonce, RESTMethods.PUT, dirtyFields)
            })
                .then(successfulSubmit)
                .catch(failedSubmit)
        }
    }

    // eslint-disable-next-line
    const invalidFormSubmit = (errors: DeepMap<FieldValues, FieldError>): void => {
        if (!isValid) {
            setActiveTabID(
                determineValidationErrors(errors).findIndex((validationError: boolean): boolean => validationError)
            )
            setIsValidationPopperOpen(true)

            setTimeout((): void => {
                if (isMounted.current) {
                    setIsValidationPopperOpen(false)
                }
            }, 2500)
        }
    }

    // List of all tabs settings.
    const tabs: Nullable<Tab[]> = [
        {
            ID: CALLRECINTEGRATIONID.CALL_REC_INTEGRATION,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(CallRecIntegration.TabGeneralTitle),
                iconName: IconName.PIN_NUM_OUTLINED,
                onClick: (): void => activateTab(CALLRECINTEGRATIONID.CALL_REC_INTEGRATION)
            },
            content: (
                <General
                    viewOnly={viewOnly}
                    isCallRecIntegrationSelected={viewOnly || !IsVarEmpty(selectedCallRecIntegration)}
                    setIntegrationType={setIntegrationType}
                />
            ),
            hasError: validationErrors[CALLRECINTEGRATIONID.CALL_REC_INTEGRATION]
        },
        {
            ID: CALLRECINTEGRATIONID.SCOPE_ASSIGNMENT,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(CallRecIntegration.TabScopeAssignmentsTitle),
                iconName: IconName.GPS_FIXED_OUTLINED,
                onClick: (): void => activateTab(CALLRECINTEGRATIONID.SCOPE_ASSIGNMENT)
            },
            content: <ScopeAssignment viewOnly={viewOnly} />,
            hasError: validationErrors[CALLRECINTEGRATIONID.SCOPE_ASSIGNMENT]
        },
        {
            ID: CALLRECINTEGRATIONID.COMMUNICATIONS_SETTINGS,
            isDisplayed: integrationType === INTEGRATION_TYPE_VALUES.UNKNOWN,
            tabsMenuItem: {
                title: language.get(CallRecIntegration.TabApiCommunicationsTitle),
                iconName: IconName.MODULE,
                onClick: (): void => activateTab(CALLRECINTEGRATIONID.COMMUNICATIONS_SETTINGS)
            },
            content: <CommunicationsSettings viewOnly={viewOnly} />,
            hasError: validationErrors[CALLRECINTEGRATIONID.COMMUNICATIONS_SETTINGS]
        },
        {
            ID: CALLRECINTEGRATIONID.DUBBER_COMMUNICATIONS_SETTINGS,
            isDisplayed: integrationType === INTEGRATION_TYPE_VALUES.DUBBER,
            tabsMenuItem: {
                title: language.get(CallRecIntegration.TabDubberCommunicationsTitle),
                iconName: IconName.MODULE,
                onClick: (): void => activateTab(CALLRECINTEGRATIONID.DUBBER_COMMUNICATIONS_SETTINGS)
            },
            content: (
                <DubberCommunicationsSettings
                    viewOnly={viewOnly}
                    selectedCallRecIntegration={selectedCallRecIntegration}
                    sessionNonce={sessionNonce}
                />
            ),
            hasError: validationErrors[CALLRECINTEGRATIONID.DUBBER_COMMUNICATIONS_SETTINGS]
        }
    ]

    return (
        <div className='configuration-tabs'>
            <FormProvider {...methods}>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(submitCRIConfiguration, invalidFormSubmit)}
                    className={isSubmitting ? HIDE : EMPTY_STRING}
                    noValidate
                >
                    {tabs && <Tabs tabs={tabs} activeTabID={activeTabID} />}
                    {!viewOnly && (
                        <FooterActions
                            serverError={serverError}
                            isValidationPopperOpen={isValidationPopperOpen}
                            closeModal={closeModal}
                        />
                    )}
                </form>
            </FormProvider>
        </div>
    )
}

export default CallRecIntegrationTabs
